
import { supabase, tables } from '@/lib/supabase';
import { FeatureRequest, FeatureRequestStatus } from '@/types/featureRequestTypes';
import { toast } from "sonner";

export class FeatureRequestService {
  /**
   * Get all feature requests
   */
  static async getAllFeatureRequests(): Promise<FeatureRequest[]> {
    try {
      const { data, error } = await tables.feature_requests()
        .select('*')
        .order('votes_count', { ascending: false });

      if (error) {
        console.error('Error fetching feature requests:', error);
        toast.error('Failed to load feature requests');
        return [];
      }

      return data as FeatureRequest[];
    } catch (error) {
      console.error('Error in getAllFeatureRequests:', error);
      toast.error('An unexpected error occurred');
      return [];
    }
  }

  /**
   * Get feature requests with vote status for the current user
   */
  static async getFeatureRequestsWithVoteStatus(userId: string): Promise<FeatureRequest[]> {
    try {
      // First get all feature requests
      const { data: requests, error: requestsError } = await tables.feature_requests()
        .select('*')
        .order('votes_count', { ascending: false });

      if (requestsError) {
        console.error('Error fetching feature requests:', requestsError);
        toast.error('Failed to load feature requests');
        return [];
      }

      // Then get all votes by this user
      const { data: votes, error: votesError } = await tables.feature_request_votes()
        .select('feature_request_id')
        .eq('user_id', userId);

      if (votesError) {
        console.error('Error fetching user votes:', votesError);
        // Continue anyway, just won't show vote status
      }

      // Create a set of feature request IDs the user has voted for
      const userVotedIds = new Set(votes?.map(vote => vote.feature_request_id) || []);

      // Enhance feature requests with vote status
      return (requests as FeatureRequest[]).map(request => ({
        ...request,
        has_voted: userVotedIds.has(request.id)
      }));
    } catch (error) {
      console.error('Error in getFeatureRequestsWithVoteStatus:', error);
      toast.error('An unexpected error occurred');
      return [];
    }
  }

  /**
   * Create a new feature request
   */
  static async createFeatureRequest(data: {
    title: string;
    description: string;
    created_by: string;
    organization_id: string;
  }): Promise<FeatureRequest | null> {
    try {
      const { data: result, error } = await tables.feature_requests()
        .insert({
          ...data,
          status: 'uncategorized',
          votes_count: 0
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating feature request:', error);
        toast.error('Failed to submit feature request');
        return null;
      }

      toast.success('Feature request submitted successfully');
      return result as FeatureRequest;
    } catch (error) {
      console.error('Error in createFeatureRequest:', error);
      toast.error('An unexpected error occurred');
      return null;
    }
  }

  /**
   * Vote for a feature request
   */
  static async voteForFeatureRequest(
    featureRequestId: string, 
    userId: string,
    organizationId: string
  ): Promise<boolean> {
    try {
      const { error } = await tables.feature_request_votes()
        .insert({
          feature_request_id: featureRequestId,
          user_id: userId,
          organization_id: organizationId
        });

      if (error) {
        console.error('Error voting for feature request:', error);
        toast.error('Failed to vote for feature request');
        return false;
      }

      toast.success('Vote recorded');
      return true;
    } catch (error) {
      console.error('Error in voteForFeatureRequest:', error);
      toast.error('An unexpected error occurred');
      return false;
    }
  }

  /**
   * Remove a vote from a feature request
   */
  static async removeVote(featureRequestId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await tables.feature_request_votes()
        .delete()
        .match({
          feature_request_id: featureRequestId,
          user_id: userId
        });

      if (error) {
        console.error('Error removing vote:', error);
        toast.error('Failed to remove vote');
        return false;
      }

      toast.success('Vote removed');
      return true;
    } catch (error) {
      console.error('Error in removeVote:', error);
      toast.error('An unexpected error occurred');
      return false;
    }
  }

  /**
   * Update a feature request's status (admin only)
   */
  static async updateFeatureRequestStatus(
    id: string,
    status: FeatureRequestStatus
  ): Promise<FeatureRequest | null> {
    try {
      const { data, error } = await tables.feature_requests()
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating feature request status:', error);
        toast.error('Failed to update feature request status');
        return null;
      }

      toast.success('Feature request status updated');
      return data as FeatureRequest;
    } catch (error) {
      console.error('Error in updateFeatureRequestStatus:', error);
      toast.error('An unexpected error occurred');
      return null;
    }
  }
}
